import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from 'components';
import { DropdownV3 } from 'components/ComponentV2';
import MultiSelect from 'components/MultiSelect';
import { Tooltip } from 'components/Tooltip/Tooltip.component';
import useScrollOffset from 'hooks/useScrollOffset';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import { useCommonList } from 'lib/contexts/common-list-context';
import { useAuth } from 'lib/providers/AuthProvider';

import styles from '../BuildProfileSection.module.scss';

interface DemographicInfoProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const DemographicInfo: React.FC<DemographicInfoProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: DemographicInfoProps) => {
    const [t] = useTranslation('candidate.profile');

    const contentClassNames = cn(
      {
        [styles.BuildProfileSection]: true,
      },
      className
    );
    const headerRef = useRef<HTMLDivElement>(null);
    const genderDropdownRef = useRef<HTMLButtonElement>(null);
    const physicalDisabilityDropdownRef = useRef<HTMLButtonElement>(null);
    const congnitiveDisabilityDropdownRef = useRef<HTMLButtonElement>(null);
    const socioEconomicStatusDropdownRef = useRef<HTMLButtonElement>(null);
    const ethnicityDropdownRef = useRef<HTMLDivElement>(null);
    const { getScrollOffset } = useScrollOffset();
    const {
      genders,
      raceEthnicity,
      physicalDisability: physicalDisabilityList,
      congnitiveDisability: congnitiveDisabilityList,
      socioEconomicStatus: socioEconomicStatusList,
    } = useCommonList();

    const { candidate } = useAuth();
    const { updateCandidate } = useCandidateProfile();

    const [gender, setGender] = useState<string | null>(null);
    const [ethnicities, setEthnicities] = useState<Array<string> | null>(null);
    const [physicalDisability, setPhysicalDisability] = useState<string | null>(
      null
    );
    const [congnitiveDisability, setCongnitiveDisability] = useState<
      string | null
    >(null);
    const [socioEconomicStatus, setSocioEconomicStatus] = useState<
      string | null
    >(null);

    useEffect(() => {
      if (candidate) {
        const {
          gender,
          ethnicities,
          physical_disability,
          congnitive_disability,
          socio_economic_status,
        } = candidate;
        setGender(gender);
        setEthnicities(ethnicities.data.map((item) => item.id));
        setPhysicalDisability(physical_disability);
        setCongnitiveDisability(congnitive_disability);
        setSocioEconomicStatus(socio_economic_status);
      }
    }, [candidate]);

    const onGenderSelect = (value: string) => {
      const gender =
        genders && genders.find((gender) => gender.label === value);
      if (gender) {
        setGender(gender?.label);

        updateCandidate({ gender: gender?.label });
      }
    };

    const onPhysicalDisabilitySelect = (value: string) => {
      const disability =
        physicalDisabilityList &&
        physicalDisabilityList.find((disability) => disability.label === value);
      if (disability) {
        setPhysicalDisability(disability?.label);
        updateCandidate({ physical_disability: disability?.label });
      }
    };

    const onCongnitiveDisabilitySelect = (value: string) => {
      const disability =
        congnitiveDisabilityList &&
        congnitiveDisabilityList.find(
          (disability) => disability.label === value
        );
      if (disability) {
        setCongnitiveDisability(disability?.label);
        updateCandidate({ congnitive_disability: disability?.label });
      }
    };

    const onSocioEconomicStatusSelect = (value: string) => {
      const economicStatus =
        socioEconomicStatusList &&
        socioEconomicStatusList.find(
          (economicStatus) => economicStatus.label === value
        );
      if (economicStatus) {
        setSocioEconomicStatus(economicStatus?.label);
        updateCandidate({
          socio_economic_status: economicStatus?.label,
        });
      }
    };

    const onRaceAndEthnicitySelect = (values: Array<string>) => {
      setEthnicities(values);
      updateCandidate({ ethnicity_ids: values });
    };

    const selectedEthnicityText = useMemo(() => {
      const labelsList = raceEthnicity
        ?.filter((item) => {
          if (ethnicities && ethnicities.includes(item.id)) return item.label;
        })
        .map((item) => item.label);
      return (
        labelsList?.join(', ') ||
        t('demographic-info.ethnicity.select-ethnicity')
      );
    }, [ethnicities]);

    const viewportRef = useRef(document.documentElement);
    const scrollToDropdown = (dropdownRef: React.RefObject<HTMLElement>) => {
      const topSpace = 12;
      const headerHeight = 62;
      const adjustment = topSpace + headerHeight;
      const offset = getScrollOffset(
        dropdownRef,
        viewportRef,
        headerRef,
        adjustment
      );
      window.scrollTo({ top: offset, behavior: 'smooth' });
    };

    const scrollToDropdownGender = () => {
      scrollToDropdown(genderDropdownRef);
    };

    const scrollToDropdownPhysical = () => {
      scrollToDropdown(physicalDisabilityDropdownRef);
    };

    const scrollToDropdownCongnitive = () => {
      scrollToDropdown(congnitiveDisabilityDropdownRef);
    };

    const scrollToDropdownSocioEconomic = () => {
      scrollToDropdown(socioEconomicStatusDropdownRef);
    };

    const scrollToDropdownEthnicity = () => {
      scrollToDropdown(ethnicityDropdownRef);
    };

    useEffect(() => {
      const event = new Event('scroll-top');

      // Function to dispatch the scroll event
      const dispatchScrollEvent = () => {
        window.dispatchEvent(event);
      };

      dispatchScrollEvent();

      const scrollInterval = setInterval(() => {
        dispatchScrollEvent();
      }, 10);

      return () => {
        clearInterval(scrollInterval);
      };
    }, []);

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="DemographicInformation"
      >
        <section className={styles.section1}>
          <span className={styles.title}>{t('demographic-info.title')}</span>
          <span>
            <Tooltip
              content={t('demographic-info.tooltip')}
              position="top"
              xAlign="center"
              xAutoAlign={true}
              color="dark"
              className={styles.tooltipContainer}
              containerClassName={styles.tooltipContainerContent}
            >
              <Icon
                iconName="icon_info"
                className={styles.tooltipContainerIcon}
                size="auto"
              />
            </Tooltip>
          </span>
        </section>
        <section className={styles.section2}>
          <section className={styles.BuildProfileSectionContent}>
            {/* gender input field */}
            <DropdownV3
              label={t('demographic-info.gender.label')}
              title={t('demographic-info.gender.title')}
              showOptionalLabel={true}
              value={gender || ''}
              onChange={(value) => onGenderSelect(value)}
              options={genders || []}
              isSearchable={false}
              selectedValueLabel={gender || ''}
              isValid={true}
              className={styles.gap}
              triggerButtonClassName={styles.trigger}
              dropdownClassName={styles.dropdown3}
              selectedValueClassName={styles.selectedValue}
              scrollToDropdown={scrollToDropdownGender}
              ref={genderDropdownRef}
            />

            {/* ethnicity input field */}
            <div className={cn(styles.gap)}>
              <div className={styles.Multiselect} ref={ethnicityDropdownRef}>
                <div className={styles.MultiselectLabel}>
                  <span>{t('demographic-info.ethnicity.label')}</span>
                </div>
                <span className={styles.optional}>
                  {' '}
                  {t('demographic-info.ethnicity.optional')}
                </span>
              </div>
              <div>
                <MultiSelect
                  title={
                    ethnicities && ethnicities.length === 0
                      ? t('demographic-info.ethnicity.select-ethnicity')
                      : selectedEthnicityText
                  }
                  placeholder={t('demographic-info.ethnicity.placeholder')}
                  options={raceEthnicity}
                  values={ethnicities || []}
                  onChange={onRaceAndEthnicitySelect}
                  className={cn(styles.dropdown)}
                  dropdownClassName={styles.multiDropdown}
                  checkBoxClassName={styles.checkbox}
                  showCount={false}
                  isSearchable
                  fullWidth
                  iconClassName="icon_arrow-down"
                  position="bottom"
                  buttonTitleClassName={styles.multiDropdownTrigger}
                  scrollToDropdown={scrollToDropdownEthnicity}
                />
              </div>
            </div>

            {/* physical disability input field */}
            <DropdownV3
              label={t('demographic-info.physical-disability.label')}
              title={t('demographic-info.physical-disability.title')}
              showOptionalLabel={true}
              value={physicalDisability || ''}
              onChange={(value) => onPhysicalDisabilitySelect(value)}
              options={physicalDisabilityList || []}
              isSearchable={false}
              selectedValueLabel={physicalDisability || ''}
              isValid={true}
              className={styles.gap}
              triggerButtonClassName={styles.trigger}
              dropdownClassName={styles.dropdown3}
              infoToolTip={
                <Tooltip
                  content={t('demographic-info.physical-disability.tooltip')}
                  position="top"
                  xAlign="center"
                  xAutoAlign={true}
                  color="dark"
                  className={cn(styles.tooltipContainer, {
                    [styles.tooltipContainerPhysical]: true,
                  })}
                  containerClassName={styles.tooltipContainerContent}
                >
                  <Icon
                    iconName="icon_info"
                    className={styles.tooltipContainerIcon}
                    size="auto"
                  />
                </Tooltip>
              }
              selectedValueClassName={styles.selectedValue}
              scrollToDropdown={scrollToDropdownPhysical}
              ref={physicalDisabilityDropdownRef}
            />

            {/* cognitive disability input field */}
            <DropdownV3
              label={t('demographic-info.cognitive-disability.label')}
              title={t('demographic-info.cognitive-disability.title')}
              showOptionalLabel={true}
              value={congnitiveDisability || ''}
              onChange={(value) => onCongnitiveDisabilitySelect(value)}
              options={congnitiveDisabilityList || []}
              isSearchable={false}
              selectedValueLabel={congnitiveDisability || ''}
              isValid={true}
              className={styles.gap}
              triggerButtonClassName={styles.trigger}
              dropdownClassName={styles.dropdown3}
              infoToolTip={
                <Tooltip
                  content={t('demographic-info.cognitive-disability.tooltip')}
                  position="top"
                  xAlign="center"
                  xAutoAlign={true}
                  color="dark"
                  className={cn(styles.tooltipContainer, {
                    [styles.tooltipContainerCognitive]: true,
                  })}
                  containerClassName={styles.tooltipContainerContent}
                >
                  <Icon
                    iconName="icon_info"
                    className={styles.tooltipContainerIcon}
                    size="auto"
                  />
                </Tooltip>
              }
              selectedValueClassName={styles.selectedValue}
              scrollToDropdown={scrollToDropdownCongnitive}
              ref={congnitiveDisabilityDropdownRef}
            />

            {/* socio-economic background input field */}
            <DropdownV3
              label={t('demographic-info.socio-economic.label')}
              title={t('demographic-info.socio-economic.title')}
              showOptionalLabel={true}
              value={socioEconomicStatus || ''}
              onChange={(value) => onSocioEconomicStatusSelect(value)}
              options={socioEconomicStatusList || []}
              isSearchable={false}
              selectedValueLabel={socioEconomicStatus || ''}
              isValid={true}
              className={styles.gap}
              triggerButtonClassName={styles.trigger}
              dropdownClassName={cn(styles.dropdown3, styles.lastDropdown)}
              infoToolTip={
                <Tooltip
                  content={t('demographic-info.socio-economic.tooltip')}
                  position="top"
                  xAlign="center"
                  xAutoAlign={true}
                  color="dark"
                  className={cn(styles.tooltipContainer)}
                  containerClassName={styles.tooltipContainerContent}
                >
                  <Icon
                    iconName="icon_info"
                    className={styles.tooltipContainerIcon}
                    size="auto"
                  />
                </Tooltip>
              }
              selectedValueClassName={styles.selectedValue}
              scrollToDropdown={scrollToDropdownSocioEconomic}
              ref={socioEconomicStatusDropdownRef}
            />
          </section>
        </section>
      </div>
    );
  }
);

DemographicInfo.displayName = 'DemographicInfo';
